import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToastContext } from '@contexts/ToastContext';
import { MailIcon } from '@components/common/ui/icons';
import { CustomSelect } from '@components/common/ui/CustomSelect';
import { useUserSearch } from '@components/modals/hooks/useUserSearch';
import type { User } from '@components/modals/hooks/useShareModal';
import { getInitials } from '@components/modals/utils';
import * as styles from '@components/modals/ShareModal.module.css';

const cn = (...classes: (string | undefined | false)[]) => classes.filter(Boolean).join(' ');

interface InviteFormProps {
    users: User[];
    loading: boolean;
    onInvite: (userId: string, permission: 'read' | 'edit') => Promise<boolean>;
}

export const InviteForm: React.FC<InviteFormProps> = ({ users, loading, onInvite }) => {
    const { t } = useTranslation();
    const toast = useToastContext();
    const [inviteEmail, setInviteEmail] = useState('');
    const [invitePermission, setInvitePermission] = useState<'read' | 'edit'>('edit');
    const [showSuggestions, setShowSuggestions] = useState(false);

    const { filteredUsers } = useUserSearch(users, inviteEmail);

    const permissionOptions = [
        { value: 'read', label: t('share.permission.view') },
        { value: 'edit', label: t('share.permission.edit') },
    ];

    const handleInvite = async () => {
        if (!inviteEmail.trim()) {
            toast.warning(t('share.toast.enterEmailOrName'));
            return;
        }

        if (filteredUsers.length === 0) {
            toast.error(t('share.toast.userNotFound'));
            return;
        }

        if (filteredUsers.length > 1) {
            toast.warning(t('share.toast.multipleUsers'));
            return;
        }

        const userToInvite = filteredUsers[0];
        const userId = userToInvite.id;

        if (!userId) {
            toast.error(t('share.toast.userIdFailed'));
            console.error('User to invite:', userToInvite);
            return;
        }

        const success = await onInvite(userId, invitePermission);

        if (success) {
            toast.success(
                t('share.toast.accessGranted', {
                    name: userToInvite.name || userToInvite.email,
                }),
            );
            setInviteEmail('');
            setShowSuggestions(false);
        }
    };

    return (
        <div className={styles.section}>
            <label className={styles.label}>{t('share.inviteByEmail')}</label>
            <div className={styles.inviteRow}>
                <div className={styles.inputWrapper}>
                    <MailIcon className={styles.inputIcon} />
                    <input
                        type="text"
                        className={styles.input}
                        placeholder={t('share.invitePlaceholder')}
                        value={inviteEmail}
                        onChange={(e) => {
                            setInviteEmail(e.target.value);
                            setShowSuggestions(true);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleInvite();
                            }
                        }}
                    />
                    {showSuggestions && inviteEmail && filteredUsers.length > 0 && (
                        <div className={styles.userSuggestions}>
                            {filteredUsers.slice(0, 5).map((user) => (
                                <button
                                    key={user.id}
                                    className={styles.userSuggestion}
                                    onClick={() => {
                                        setInviteEmail(user.email || user.name || '');
                                        setShowSuggestions(false);
                                    }}
                                >
                                    <div className={styles.userSuggestionAvatar}>
                                        {getInitials(user.name || user.email || 'U')}
                                    </div>
                                    <div className={styles.userSuggestionInfo}>
                                        <div className={styles.userSuggestionName}>{user.name}</div>
                                        <div className={styles.userSuggestionEmail}>
                                            {user.email}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <CustomSelect
                    value={invitePermission}
                    options={permissionOptions}
                    onChange={(value) => setInvitePermission(value as 'read' | 'edit')}
                />
                <button
                    className={cn(styles.button, styles.buttonPrimary)}
                    onClick={handleInvite}
                    disabled={loading || !inviteEmail.trim()}
                >
                    {t('share.invite')}
                </button>
            </div>
        </div>
    );
};
